import type { FC, PropsWithChildren, ReactElement, ReactNode } from 'react'
import { useEffect } from 'react'
import { Box, Dialog as MuiDialog, DialogTitle as MuiDialogTitle, IconButton, Typography } from '@mui/material'
import { useTheme } from '@dhl/theme'

import { Close } from '@dhl/icons'

import { BottomDialog } from './BottomDialog.js'

type DialogTitleProps = PropsWithChildren<{
  onClose?: () => void
  isMobile: boolean
}>

const DialogTitle = ({ isMobile, onClose, children }: DialogTitleProps) => {
  if (isMobile) {
    return <MuiDialogTitle sx={{ alignSelf: 'start', mt: 1 }}>{children}</MuiDialogTitle>
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderBottom={(theme) => `1px solid ${theme.palette.grey[300]}`}
      px={{ xs: 2, sm: 3 }}
      py={0.5}
    >
      {children}

      {onClose && (
        <IconButton
          data-test="dialog-title-closable"
          sx={{ marginLeft: 'auto', marginRight: '-10px', zIndex: 1 }}
          onClick={() => onClose()}
        >
          <Close fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  )
}

export type DialogProps = {
  open: boolean
  title?: string | ReactElement

  onOpen?: () => void

  /**
   * Close our dialog
   */
  onClose?: () => void

  /**
   * Reject the confirmation
   */
  onReject?: () => void

  testId?: string
  children?: ReactNode
  disableBackdropClick?: boolean
  disableEscapeKeyDown?: boolean

  /**
   * The modal will not restore focus to previously focused element once modal is hidden or unmounted when true
   * @default false
   */
  disableRestoreFocus?: boolean

  /**
   * For mobile
   */
  onBackdropClick?: () => void

  /**
   * For mobile
   */
  onEscapeKeyDown?: () => void
}

/**
 * When you need the Container logic of the Confirmation Dialog
 * This creates a Modal on big screens and a Bottom Dialog on smaller devices
 *
 * You can place any own content inside these dialogs which still needs to have the option
 * to confirm or to cancel inside, but thats logic you can now create by yourself
 */
export const Dialog: FC<DialogProps> = ({
  open,
  title,
  testId,
  children,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  disableRestoreFocus = false,
  onClose,
  onOpen,
  onReject,
  onBackdropClick,
  onEscapeKeyDown,
}) => {
  const {
    device: { isMobile },
  } = useTheme()

  const Container = isMobile ? BottomDialog : MuiDialog

  useEffect(() => {
    if (open) {
      onOpen?.()
    }
  }, [onOpen, open])

  const handleClose = (_?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      if (onBackdropClick) {
        onBackdropClick()
      } else if (!disableBackdropClick) {
        onReject?.()
      }
    } else if (reason === 'escapeKeyDown') {
      if (onEscapeKeyDown) {
        onEscapeKeyDown()
      } else if (!disableEscapeKeyDown) {
        onReject?.()
      }
    }
    onClose?.()

    if (!reason && !onClose) {
      onReject?.()
    }
  }

  return (
    <Container
      open={open}
      onClose={handleClose}
      // @deprecated please do not use this
      data-test={testId}
      // prefered
      data-testid={testId}
      maxWidth={isMobile ? undefined : 'xs'}
      disableRestoreFocus={disableRestoreFocus}
      fullWidth
    >
      {title ? (
        <DialogTitle onClose={handleClose} isMobile={isMobile}>
          <Typography variant={isMobile ? 'h5' : 'h4'} color="common.black" component="span">
            {title}
          </Typography>
        </DialogTitle>
      ) : null}
      {children}
    </Container>
  )
}
